import {
  Badge,
  Box,
  Container,
  Divider,
  Heading,
  Image,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import logo from "../../assets/senca/logo.jpg";
import { getExecutionByToken, getExecutionImageUrl, isVideoMedia } from "./osExecutionService";

const formatDateTime = (value) => {
  if (!value) {
    return "N/A";
  }
  return new Date(value).toLocaleString("pt-BR");
};

export default function PublicOsExecution() {
  const { token } = useParams();
  const [execution, setExecution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    const loadExecution = async () => {
      if (!token) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      try {
        const data = await getExecutionByToken(token);
        if (!data) {
          setNotFound(true);
          return;
        }
        setExecution(data);
        setNotFound(false);
        setLoadError("");
      } catch (error) {
        console.error("Erro ao carregar relatório público:", error);
        setExecution(null);
        setNotFound(false);
        setLoadError(
          error.response?.data?.message ||
            "Não foi possível carregar o relatório. Tente novamente."
        );
      } finally {
        setLoading(false);
      }
    };

    loadExecution();
  }, [token]);

  if (loading) {
    return (
      <Box minH="100vh" bg="gray.50" py={16}>
        <Container maxW="720px" bg="white" p={8} borderRadius="xl" shadow="md">
          <VStack spacing={4}>
            <Spinner color="#C1272D" />
            <Text>Carregando relatório...</Text>
          </VStack>
        </Container>
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box minH="100vh" bg="gray.50" py={16}>
        <Container maxW="720px" bg="white" p={8} borderRadius="xl" shadow="md">
          <VStack spacing={4}>
            <Image src={logo} alt="Senca Serviços e Engenharia" maxH="56px" />
            <Heading size="md" color="#C1272D">
              Erro ao carregar relatório
            </Heading>
            <Text color="gray.600" textAlign="center">
              {loadError}
            </Text>
            <Text
              as={RouterLink}
              to="/"
              color="#C1272D"
              fontWeight="semibold"
            >
              Voltar ao site
            </Text>
          </VStack>
        </Container>
      </Box>
    );
  }

  if (notFound || !execution) {
    return (
      <Box minH="100vh" bg="gray.50" py={16}>
        <Container maxW="720px" bg="white" p={8} borderRadius="xl" shadow="md">
          <VStack spacing={4}>
            <Image src={logo} alt="Senca Serviços e Engenharia" maxH="56px" />
            <Heading size="md" color="#C1272D">
              Relatório não encontrado
            </Heading>
            <Text color="gray.600" textAlign="center">
              Este link pode estar inválido ou a execução ainda não foi
              registrada.
            </Text>
            <Text
              as={RouterLink}
              to="/"
              color="#C1272D"
              fontWeight="semibold"
            >
              Voltar ao site
            </Text>
          </VStack>
        </Container>
      </Box>
    );
  }

  const snapshot = execution.osSnapshot || {};

  return (
    <Box minH="100vh" bg="gray.50" py={10}>
      <Container maxW="900px">
        <Box bg="white" borderRadius="xl" shadow="lg" overflow="hidden">
          <Box bg="#C1272D" color="white" px={8} py={6}>
            <Image src={logo} alt="Senca Serviços e Engenharia" maxH="52px" mb={4} />
            <Heading size="lg">Relatório de Execução de Serviço</Heading>
            <Text mt={2} opacity={0.9}>
              Visualização pública — OS #{execution.orderNumber}
            </Text>
          </Box>

          <Box px={8} py={8}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
              <Box>
                <Text fontSize="sm" color="gray.500">
                  Executado por
                </Text>
                <Text fontWeight="semibold">
                  {execution.executedBy?.name ||
                    execution.executedBy?.login ||
                    "N/A"}
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">
                  Data da execução
                </Text>
                <Text fontWeight="semibold">
                  {formatDateTime(execution.executedAt)}
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">
                  Tipo de serviço
                </Text>
                <Text fontWeight="semibold">
                  {snapshot.serviceTypeLabel || snapshot.summary || "N/A"}
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">
                  Situação
                </Text>
                <Badge colorScheme="green">
                  {snapshot.situationLabel || "Executado"}
                </Badge>
              </Box>
            </SimpleGrid>

            <Divider mb={6} />

            <VStack align="stretch" spacing={5}>
              <Box>
                <Heading size="sm" mb={2} color="#625752">
                  Resumo da execução
                </Heading>
                <Box p={4} bg="gray.50" borderRadius="md">
                  <Text>{execution.executionSummary}</Text>
                </Box>
              </Box>

              {execution.executionNotes && (
                <Box>
                  <Heading size="sm" mb={2} color="#625752">
                    Observações
                  </Heading>
                  <Box p={4} bg="gray.50" borderRadius="md">
                    <Text>{execution.executionNotes}</Text>
                  </Box>
                </Box>
              )}

              <Box>
                <Heading size="sm" mb={2} color="#625752">
                  Detalhes da OS
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                  <Text>
                    <strong>Solicitante:</strong>{" "}
                    {snapshot.requesterName || "N/A"}
                  </Text>
                  <Text>
                    <strong>Unidade:</strong> {snapshot.unityName || "N/A"}
                  </Text>
                  <Text gridColumn={{ md: "span 2" }}>
                    <strong>Endereço:</strong> {snapshot.address || "N/A"}
                  </Text>
                  <Text gridColumn={{ md: "span 2" }}>
                    <strong>Descrição:</strong> {snapshot.details || "N/A"}
                  </Text>
                </SimpleGrid>
              </Box>

              {execution.images?.length > 0 && (
                <Box>
                  <Heading size="sm" mb={3} color="#625752">
                    Fotos do serviço realizado
                  </Heading>
                  <SimpleGrid columns={[1, 2, 3]} spacing={4}>
                    {execution.images.map((image) =>
                      isVideoMedia(image) ? (
                        <Box
                          key={image.imageId}
                          as="video"
                          src={getExecutionImageUrl(image.url)}
                          controls
                          borderRadius="lg"
                          border="1px solid #DDD"
                          maxH="240px"
                          w="100%"
                        />
                      ) : (
                        <Image
                          key={image.imageId}
                          src={getExecutionImageUrl(image.url)}
                          alt={image.originalFileName || "Foto do serviço"}
                          borderRadius="lg"
                          border="1px solid #DDD"
                          objectFit="cover"
                          maxH="240px"
                          w="100%"
                        />
                      )
                    )}
                  </SimpleGrid>
                </Box>
              )}
            </VStack>
          </Box>
        </Box>

        <Text mt={6} textAlign="center" fontSize="sm" color="gray.500">
          Senca Serviços e Engenharia — relatório gerado pelo sistema de ordens de serviço
        </Text>
      </Container>
    </Box>
  );
}
